import { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { updatePaymentMethods } from 'store/AuthSlice'
import { useCurrentUser } from './AuthHooks'
import { useLazyGetProfileQuery } from 'apis/UserApi'
import {
  useAddCreditCardMutation,
  useDeleteCreditCardMutation,
  useSetDefaultPaymentMethodMutation,
  AddCreditCardRequest,
} from 'apis/PaymentMethodApi'

export const useCreditCard = () => {
  const { currentUser } = useCurrentUser()
  const [getProfile] = useLazyGetProfileQuery()
  const [addCreditCard] = useAddCreditCardMutation()
  const [deleteCreditCardMutation] = useDeleteCreditCardMutation()
  const [setDefaultPaymentMethod, { isLoading: isSettingDefault }] =
    useSetDefaultPaymentMethodMutation()
  const dispatch = useDispatch()

  const [isDeleting, setIsDeleting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const saveCreditCard = useCallback(
    async (creditCardData: AddCreditCardRequest) => {
      const sanitizedCardData = {
        ...creditCardData,
        number: creditCardData.number.replace(/\s+/g, ''),
      }

      setIsSaving(true)

      const { id } = await addCreditCard(sanitizedCardData).unwrap()

      const { data: userProfile } = await getProfile({})

      if (userProfile?.paymentMethods) {
        dispatch(updatePaymentMethods(userProfile.paymentMethods))
      }

      setIsSaving(false)

      return id
    },
    [addCreditCard, getProfile, dispatch],
  )

  const setDefaultCard = useCallback(
    async (cardId: string) => {
      if (!cardId) return

      await setDefaultPaymentMethod(cardId).unwrap()

      if (currentUser?.paymentMethods) {
        dispatch(
          updatePaymentMethods(
            currentUser.paymentMethods.map((card) => ({
              ...card,
              default: card.id === cardId,
            })),
          ),
        )
      }
    },
    [currentUser?.paymentMethods, dispatch, setDefaultPaymentMethod],
  )

  const deleteCreditCard = useCallback(
    async (cardId?: string) => {
      if (!cardId) return

      setIsDeleting(true)
      await deleteCreditCardMutation(cardId)

      if (currentUser?.paymentMethods) {
        const remainingCards = currentUser.paymentMethods.filter(
          (card) => card.id !== cardId,
        )

        if (
          remainingCards.length > 0 &&
          !remainingCards.some((card) => card.default)
        ) {
          // Set the first card as default via API
          await setDefaultPaymentMethod(remainingCards[0].id).unwrap()

          // Update local state with the new default card
          dispatch(
            updatePaymentMethods(
              remainingCards.map((card, index) => ({
                ...card,
                default: index === 0,
              })),
            ),
          )
        } else {
          // If no need to set default, just update the local state
          dispatch(updatePaymentMethods(remainingCards))
        }
      }
      setIsDeleting(false)
    },
    [
      deleteCreditCardMutation,
      dispatch,
      currentUser?.paymentMethods,
      setDefaultPaymentMethod,
    ],
  )

  return {
    isSaving,
    isDeleting,
    isSettingDefault,
    saveCreditCard,
    setDefaultCard,
    deleteCreditCard,
  }
}
